/**
 * Skeleton Loader Components
 * Provides shimmer loading states for various content types
 */
import React from 'react';

// Base Skeleton component
export function Skeleton({ width = '100%', height = '20px', borderRadius = '4px', style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius,
        background: 'linear-gradient(90deg, #f5f5f5 0%, #eeeeee 50%, #f5f5f5 100%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        ...style
      }}
    />
  );
}

// Card skeleton
export function SkeletonCard({ style = {} }) {
  return (
    <div
      style={{
        padding: '24px',
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        ...style
      }}
    >
      <Skeleton height="24px" width="60%" style={{ marginBottom: '16px' }} />
      <Skeleton height="16px" width="100%" style={{ marginBottom: '8px' }} />
      <Skeleton height="16px" width="90%" style={{ marginBottom: '8px' }} />
      <Skeleton height="16px" width="70%" />
    </div>
  );
}

// Module list skeleton
export function SkeletonModuleList({ count = 5 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            padding: '16px',
            background: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <Skeleton width="40px" height="40px" borderRadius="50%" />
          <div style={{ flex: 1 }}>
            <Skeleton height="18px" width="70%" style={{ marginBottom: '8px' }} />
            <Skeleton height="14px" width="40%" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Block content skeleton
export function SkeletonBlock() {
  return (
    <div
      style={{
        padding: '24px',
        background: '#fff',
        borderRadius: '8px',
        border: '2px solid #e0e0e0',
        marginBottom: '24px'
      }}
    >
      <Skeleton height="32px" width="50%" style={{ marginBottom: '24px' }} />
      <Skeleton height="16px" width="100%" style={{ marginBottom: '12px' }} />
      <Skeleton height="16px" width="95%" style={{ marginBottom: '12px' }} />
      <Skeleton height="16px" width="85%" style={{ marginBottom: '24px' }} />
      <Skeleton height="200px" width="100%" borderRadius="8px" />
    </div>
  );
}

// Analytics card skeleton
export function SkeletonAnalyticsCard() {
  return (
    <div
      style={{
        padding: '24px',
        background: '#fff',
        borderRadius: '8px',
        border: '2px solid #e0e0e0',
        textAlign: 'center'
      }}
    >
      <Skeleton height="40px" width="40px" borderRadius="50%" style={{ margin: '0 auto 16px' }} />
      <Skeleton height="48px" width="60%" style={{ margin: '0 auto 12px' }} />
      <Skeleton height="16px" width="80%" style={{ margin: '0 auto' }} />
    </div>
  );
}

// Quiz skeleton
export function SkeletonQuiz() {
  return (
    <div
      style={{
        padding: '24px',
        background: '#fff',
        border: '2px solid #d4af37',
        borderRadius: '8px'
      }}
    >
      <Skeleton height="28px" width="40%" style={{ marginBottom: '16px' }} />
      <Skeleton height="14px" width="60%" style={{ marginBottom: '32px' }} />

      {Array.from({ length: 3 }).map((_, qIdx) => (
        <div key={qIdx} style={{ marginBottom: '32px' }}>
          <Skeleton height="20px" width="30%" style={{ marginBottom: '12px' }} />
          <Skeleton height="16px" width="90%" style={{ marginBottom: '16px' }} />

          {Array.from({ length: 4 }).map((_, oIdx) => (
            <div
              key={oIdx}
              style={{
                padding: '12px',
                marginBottom: '8px',
                border: '2px solid #ddd',
                borderRadius: '4px',
                background: '#fff'
              }}
            >
              <Skeleton height="16px" width="70%" />
            </div>
          ))}
        </div>
      ))}

      <Skeleton height="48px" width="100%" borderRadius="4px" />
    </div>
  );
}

// Add global shimmer animation
if (typeof window !== 'undefined' && !document.getElementById('skeleton-animations')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'skeleton-animations';
  styleSheet.textContent = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default Skeleton;
